import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { IconSelector } from "@tabler/icons-react";
import type { FC } from "react";
import { Button as AriaButton, MenuTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { WorkspaceId, WorkspaceOption } from "./workspaces.js";

export interface WorkspaceSwitcherProps {
  workspaces: readonly WorkspaceOption[];
  selectedWorkspaceId: WorkspaceId;
}

export const WorkspaceSwitcher: FC<WorkspaceSwitcherProps> = ({
  workspaces,
  selectedWorkspaceId,
}) => {
  const selectedWorkspace =
    workspaces.find((workspace) => workspace.id === selectedWorkspaceId) ??
    workspaces[0];

  if (!selectedWorkspace) {
    return null;
  }

  return (
    <MenuTrigger>
      <AriaButton
        aria-label={`Switch workspace (current: ${selectedWorkspace.label})`}
        className={twMerge(
          "group flex h-9 w-full flex-none items-center gap-2 rounded-lg bg-card px-2.5 text-left text-sm leading-5 font-medium text-sidebar-foreground shadow-xs transition-colors",
          "hover:bg-background hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        )}
      >
        <span className="flex size-5 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors group-hover:text-sidebar-foreground">
          <selectedWorkspace.Icon aria-hidden className="size-4 shrink-0" />
        </span>
        <span className="min-w-0 flex-1 truncate">
          {selectedWorkspace.label}
        </span>
        <IconSelector
          aria-hidden
          className="size-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-sidebar-foreground"
        />
      </AriaButton>
      <SimpleMenuPopover>
        <SimpleMenu aria-label="Switch workspace">
          {workspaces.map((workspace) => (
            <SimpleMenuItem
              key={workspace.id}
              id={workspace.id}
              textValue={workspace.label}
              href={workspace.href}
            >
              <span className="size-4 shrink-0 text-foreground">
                <workspace.Icon aria-hidden className="size-4 shrink-0" />
              </span>
              <span className="min-w-0 flex-1 truncate">{workspace.label}</span>
            </SimpleMenuItem>
          ))}
        </SimpleMenu>
      </SimpleMenuPopover>
    </MenuTrigger>
  );
};
